import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Link } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { EmptyState } from '@homecare/ui';
import { useSession } from '../../lib/session';

import { searchableFor } from './navigation';

/**
 * The ⌘K palette — scaffold (§12).
 *
 * Jump-to-anything is §25.2's job and needs clients, caregivers and visits,
 * none of which exist yet. What ships now is the shell: the shortcut, the
 * dialog, the focus trap, and search across the sections the caller can
 * actually reach.
 *
 * It searches the *filtered* navigation, not the whole map. A palette that
 * found "Billing" for a scheduler who cannot open it would undo the hiding rule
 * three lines of code away from where it was applied.
 */
export function CommandPalette(): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const permissions = useSession((state) => state.permissions);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((was) => !was);
      }
    };

    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, []);

  const items = searchableFor(permissions).filter((item) =>
    query.trim() === ''
      ? true
      : `${item.section} ${item.label}`.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="border-line bg-surface-2 text-ink-3 hover:border-line-strong duration-fast ease-calm min-h-row-default flex w-full min-w-0 items-center gap-2 rounded-md border px-3 text-left text-sm transition-colors sm:w-72"
        >
          <Search aria-hidden="true" className="h-4 w-4 flex-none" />
          {/* Names what is searchable rather than the act of searching. Today
              that is the sections; the placeholder says the nouns it will
              search when clients and caregivers exist, because a field labelled
              "Search…" tells nobody what it will find. */}
          <span className="flex-1 truncate">Search clients, caregivers…</span>
          <kbd className="border-line bg-surface text-ink-3 text-2xs rounded-sm border px-1.5 py-0.5 font-mono">
            Ctrl K
          </kbd>
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="bg-overlay fixed inset-0" />
        <Dialog.Content className="border-line bg-surface shadow-e4 fixed left-1/2 top-24 w-[min(34rem,calc(100vw-2rem))] -translate-x-1/2 rounded-lg border">
          <Dialog.Title className="sr-only">Search</Dialog.Title>
          <VisuallyHidden asChild>
            <Dialog.Description>
              Search the sections you have access to. Press Escape to close.
            </Dialog.Description>
          </VisuallyHidden>

          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Go to…"
            aria-label="Search sections"
            className="border-line text-md text-ink placeholder:text-ink-3 w-full rounded-t-lg border-b bg-transparent px-4 py-3 font-sans"
          />

          {items.length === 0 ? (
            <div className="px-4 pb-2">
              <EmptyState
                title="Nothing matches that"
                explanation="This searches the parts of the console you can open. Try a section name — Schedule, Visits, People."
              />
            </div>
          ) : (
            <ul className="max-h-72 overflow-y-auto py-1">
              {items.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="text-ink hover:bg-surface-2 flex items-baseline justify-between gap-4 px-4 py-2 text-sm"
                  >
                    <span>{item.label}</span>
                    <span className="eyebrow">{item.section}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
